import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { t } from 'i18next';
import { RemoveAdPayload } from '../Settings/Settings.types';
import * as S from './DeleteAdSheet.styles';
import { useBodyOverflow } from '@/hooks/useBodyOverflow';
import { BottomSheet } from '@/components/UI/BottomSheet/BottomSheet';
import { DeleteReason } from '@/api/types/ads.types';
import { Checkbox } from '@/components/UI/Checkbox/Checkbox';
import { sleep } from '@/utils/helpers';
import { DRAWER_TRANSITION_TIME_MS } from '@/components/Drawer/Drawer.constants';
import { Button } from '@/components/UI/Button/Button';
import { Typography } from '@/components/UI/Typography/Typography.styles';
import { Textarea } from '@/components/UI/Textarea/Textarea';
import { AdPageTestIds } from '@/__e2e__/testIds/ad-page';

const DESCRIPTION_LIMIT = 500;

interface DeleteAdSheetProps {
  isOpen: boolean;
  toggle: () => void;
  submit: (payload: RemoveAdPayload) => void;
  reasons: DeleteReason[];
}

export function DeleteAdSheet({
  isOpen,
  toggle,
  submit,
  reasons = [],
}: DeleteAdSheetProps) {
  const [description, setDescription] = useState('');
  const [selected, setSelected] = useState<DeleteReason | null>(null);
  const [isConfirmView, setConfirmView] = useState(false);

  useBodyOverflow(isOpen);

  const isDescriptionView = selected?.descriptionRequired ?? false;

  const handleClose = useCallback(() => {
    toggle();
    sleep(DRAWER_TRANSITION_TIME_MS).then(() => {
      setSelected(null);
      setDescription('');
      setConfirmView(false);
    });
  }, [toggle]);

  const handleSubmit = () => {
    if (selected) {
      submit({
        id: selected.id,
        name: selected.name,
        delete_reason_description: description,
      });
    }
  };

  useEffect(() => {
    if (selected) {
      setConfirmView(!selected.descriptionRequired);
    }
  }, [selected]);

  const getSheetTitle = useMemo(() => {
    if (isConfirmView) return t('pages.ad.expire_confirm');
    if (isDescriptionView) return t('pages.ad.expire_reason');
    return t('pages.ad.expire');
  }, [isConfirmView, isDescriptionView]);

  const renderConfirmView = () => (
    <>
      <S.TextContainer>
        <Typography.Body $variant="2">
          {t('pages.ad.expire_confirm_body')}
        </Typography.Body>
      </S.TextContainer>

      <S.SubmitButtonContainer>
        <Button
          data-testid={AdPageTestIds.remove.submitButton}
          appearance="primary"
          isFullWidth
          onClick={handleSubmit}
        >
          {t('pages.ad.expire_submit')}
        </Button>
      </S.SubmitButtonContainer>
    </>
  );

  const renderDescriptionView = () => (
    <>
      <S.Body>
        <S.DescriptionHeader>
          <Typography.Caption $variant="2">
            {t('pages.ad.expire_reason_label')}
          </Typography.Caption>
          <Typography.Caption $variant="1">
            {t('pages.ad.expire_reason_limit', {
              count: DESCRIPTION_LIMIT - description.length,
            })}
          </Typography.Caption>
        </S.DescriptionHeader>

        <Textarea
          rows={5}
          value={description}
          maxSymbolsCount={DESCRIPTION_LIMIT}
          onChange={e => setDescription(e.target.value)}
          data-testId={AdPageTestIds.remove.descriptionInput}
          placeholder={t('pages.ad.expire_reason_placeholder')}
        />
      </S.Body>
      <S.SubmitButtonContainer>
        <Button
          data-testid={AdPageTestIds.remove.continueButton}
          appearance="primary"
          isFullWidth
          onClick={() => setConfirmView(true)}
        >
          {t('pages.ad.expire_continue')}
        </Button>
      </S.SubmitButtonContainer>
    </>
  );

  return (
    <BottomSheet
      dataTestId={AdPageTestIds.remove.sheet}
      isOpen={isOpen}
      close={handleClose}
      title={getSheetTitle}
    >
      {isConfirmView
        ? renderConfirmView()
        : isDescriptionView
        ? renderDescriptionView()
        : reasons.map(reason => (
            <S.Item key={reason.id} data-testid={`remove-reason-${reason.id}`}>
              <Checkbox
                size="md"
                value={String(reason.id) === String(selected?.id)}
                label={reason.name}
                appearance="radio"
                onChange={e =>
                  setSelected(e.currentTarget.checked ? reason : null)
                }
              />
            </S.Item>
          ))}
    </BottomSheet>
  );
}
